/**
 * Helpers for UserOffices factory methods
 */

import type { IBaseNotary } from "../../BaseNotaryFactory";
import type { UserOfficeAffiliation } from "le-coffre-resources/dist/Notary";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { buildEmptyPayload, buildSetActiveOfficePayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

/**
 * Create get user offices method
 */
export function createGetUserOfficesMethod(
	baseNotary: IBaseNotary,
	usersUrl: string,
): (userUid: string) => Promise<UserOfficeAffiliation[]> {
	return async (userUid: string): Promise<UserOfficeAffiliation[]> => {
		const url = new URL(usersUrl.concat(`/${userUid}/offices`));
		try {
			const result = await baseNotary.getRequest<UserOfficeAffiliation[]>(url);
			if (!Array.isArray(result)) {
				LoggerService.error("❌ getUserOffices: response is not an array", { userUid, result });
				return [];
			}
			return result;
		} catch (err) {
			LoggerService.error("❌ getUserOffices error", { userUid, error: err });
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

function logSetActiveOfficeRequest(
	url: string,
	userUid: string,
	officeUid: string,
	requestBody: unknown,
): void {
	LoggerService.info("🔍 [UserOfficesApi] setActiveOffice request", {
		url,
		userUid,
		officeUid,
		requestBody,
	});
}

function logSetActiveOfficeSuccess(result: {
	message: string;
	office_uid: string;
	accessToken?: string;
	refreshToken?: string;
}): void {
	LoggerService.info("🔍 [UserOfficesApi] setActiveOffice response received", {
		hasResult: !!result,
		hasMessage: !!result?.message,
		hasOfficeUid: !!result?.office_uid,
		hasAccessToken: !!result?.accessToken,
		hasRefreshToken: !!result?.refreshToken,
	});
}

function logSetActiveOfficeError(err: unknown, userUid: string, officeUid: string): void {
	LoggerService.error("❌ [UserOfficesApi] setActiveOffice error", {
		userUid,
		officeUid,
		error: err,
		errorType: typeof err,
		hasHttpStatus: typeof (err as { http_status?: number })?.http_status === "number",
		httpStatus: (err as { http_status?: number })?.http_status,
		hasMessage: typeof (err as { message?: string })?.message === "string",
		message: (err as { message?: string })?.message,
	});
}

/**
 * Create set active office method
 */
export function createSetActiveOfficeMethod(
	baseNotary: IBaseNotary,
	usersUrl: string,
): (
		userUid: string,
		officeUid: string,
		menuOfficeUidForGuestJwt?: string | null,
	) => Promise<{ message: string; office_uid: string; accessToken?: string; refreshToken?: string }> {
	return async (
		userUid: string,
		officeUid: string,
		menuOfficeUidForGuestJwt?: string | null,
	): Promise<{ message: string; office_uid: string; accessToken?: string; refreshToken?: string }> => {
		const url = new URL(usersUrl.concat(`/${userUid}/offices/active`));
		const requestBody = buildSetActiveOfficePayload(officeUid, menuOfficeUidForGuestJwt);

		logSetActiveOfficeRequest(url.toString(), userUid, officeUid, requestBody);

		try {
			const result = await baseNotary.putRequest<{
				message: string;
				office_uid: string;
				accessToken?: string;
				refreshToken?: string;
			}>(url, requestBody);

			logSetActiveOfficeSuccess(result);
			return result;
		} catch (err) {
			logSetActiveOfficeError(err, userUid, officeUid);
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get active office method
 */
export function createGetActiveOfficeMethod(
	baseNotary: IBaseNotary,
	usersUrl: string,
): (userUid: string) => Promise<UserOfficeAffiliation> {
	return async (userUid: string): Promise<UserOfficeAffiliation> => {
		const url = new URL(usersUrl.concat(`/${userUid}/offices/active`));
		try {
			return await baseNotary.getRequest<UserOfficeAffiliation>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create sync user offices method
 */
export function createSyncUserOfficesMethod(
	baseNotary: IBaseNotary,
): (userUid: string) => Promise<UserOfficeAffiliation[]> {
	return async (userUid: string): Promise<UserOfficeAffiliation[]> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/offices/sync/${userUid}`));
		try {
			const result = await baseNotary.postRequest<UserOfficeAffiliation[]>(url, buildEmptyPayload());
			if (!Array.isArray(result)) {
				LoggerService.error("❌ syncUserOffices: response is not an array", { userUid, result });
				return [];
			}
			return result;
		} catch (err) {
			LoggerService.error("❌ syncUserOffices error", { userUid, error: err });
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}
